import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import Select from '@/common/components/Select'
import TextField from '@/common/components/TextField'
import { UnitTextField } from '@/common/components/UnitTextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useInternalFieldArray } from '@/common/hooks/useInternalFieldArray'
import { Mapper } from '@/common/services/mapper'
import { cn } from '@/common/utils/cn'
import { formatCurrency, formatPercentage } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useFeeTemplates } from '@/features/feeTemplates/hooks/useFeeTemplates'
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type'
import { useUpdateContractFeeDetails } from '@/features/privateCompanies/hooks/contract/useUpdateContractFeeDetails'
import {
  UpdateContractFeeDetailsSchema,
  updateContractFeeDetailsSchema,
} from '@/features/privateCompanies/schemas/contract/updateFeeDetails.schema'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { ChangeEvent, useEffect, useState, useRef } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

interface UpdateFeeDetailsModalProps {
  contract?: CompanyContractType
  companyKind?: string
  contractUpdatedAt?: number
}

export const UpdateFeeDetailsModal = ({ contract, companyKind, contractUpdatedAt }: UpdateFeeDetailsModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [bodyClassName, setBodyClassName] = useState("overflow-visible")

  const bodyRef = useRef<any>(null)

  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('')

  const [deletedFees, setDeletedFees] = useState<{ id: string; _destroy: boolean }[]>([])

  const { data: feeTemplateOptions } = useFeeTemplates({
    filters: {
      search: searchFeeTemplate,
      category: companyKind,
      pageSize: searchFeeTemplate ? 200 : 50,
    },
    mapper: Mapper.mapToOptionsWithItem<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const formMethods = useForm<UpdateContractFeeDetailsSchema>({
    resolver: zodResolver(updateContractFeeDetailsSchema),
    defaultValues: {
      contract: {
        contract_fees_attributes:
          contract?.contract_fees?.map((fee) => ({
            id: fee.id,
            fee_template_id: fee.fee_template.id,
            display_name: fee.fee_template.display_name,
            value:
              fee.fee_template.calculate_as === 'percentage'
                ? formatPercentage(Number(fee.value))
                : formatCurrency(Number(fee.value)),
            calculate_as: fee.fee_template.calculate_as,
            main_fee: fee.main_fee,
          })) || [],
      },
    }
  })

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = formMethods

  const {
    fields: contractFeesFields,
    append: appendContractFee,
    remove: removeContractFee,
    update: updateContractFee,
  } = useInternalFieldArray({
    ...formMethods,
    name: 'contract.contract_fees_attributes',
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['clientContract', contract?.id] })
    setIsOpen(false)
  }

  const {
    mutate: updateContractFeeDetails,
    isPending: updateContractFeeDetailsIsPending,
  } = useUpdateContractFeeDetails({
    contract,
    onSuccess,
    onError: FormTransformers.errorsTransformer<UpdateContractFeeDetailsSchema>({ setError, setMessages })
  })

  const onSubmit: SubmitHandler<UpdateContractFeeDetailsSchema> = () => {
    updateContractFeeDetails({ data: watch(), deletedFees })
  }

  function resetForm() {
    setDeletedFees([]);

    reset({
      contract: {
        contract_fees_attributes:
          contract?.contract_fees?.map((fee) => ({
            id: fee.id,
            fee_template_id: fee.fee_template.id,
            display_name: fee.fee_template.display_name,
            value:
              fee.fee_template.calculate_as === 'percentage'
                ? formatPercentage(Number(fee.value))
                : formatCurrency(Number(fee.value)),
            calculate_as: fee.fee_template.calculate_as,
            main_fee: fee.main_fee,
          })) || [],
      },
    })
  }

  useEffect(resetForm, [isOpen, contractUpdatedAt])

  useEffect(() => {
    const updateBodyClassName = () => {
      if (bodyRef.current) {
        const maxHeight = window.innerHeight - 160
        const contentHeight = bodyRef.current.scrollHeight

        if (contentHeight > maxHeight) {
          setBodyClassName("overflow-y-auto")
        } else {
          setBodyClassName("overflow-visible")
        }
      }
    }

    if (isOpen) {
      updateBodyClassName()
      const observer = new ResizeObserver(updateBodyClassName)
      if (bodyRef.current) {
        observer.observe(bodyRef.current)
      }

      return () => observer.disconnect()
    }
  }, [isOpen, contractFeesFields.length])

  return (
    <Modal
      className='w-[50vw] max-w-[700px]'
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_fee_details_modal"
          operationKlassToCheckPermission={'Admin::Client::Contracts::UpdateContractFeesInformation'}
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando Tarifas"
      dataTestid="update_fee_details"
      bodyClassName={cn("max-h-[calc(100vh-10rem)]", bodyClassName)}
      useCancellableFooter
      confirmButtonClassName="bg-blue-700 rounded-br-lg"
      cancelButtonClassName="bg-white-200 rounded-bl-lg"
      disabled={updateContractFeeDetailsIsPending}
      confirmButtonIsLoading={updateContractFeeDetailsIsPending}
      body={
        <>
          <form ref={bodyRef} className="flex flex-col gap-y-4 text-white-800">
            <Select
              name='search_fee_template'
              placeholder="Pesquisar Tarifa"
              value={null}
              onChange={(newValue: any) => {
                const feeTemplate = newValue.item as FeeTemplateData

                appendContractFee({
                  display_name: feeTemplate.display_name,
                  calculate_as: feeTemplate.calculate_as,
                  fee_template_id: feeTemplate.id,
                  id: '',
                  value: feeTemplate.default_value
                    ? feeTemplate.calculate_as === 'percentage'
                      ? formatPercentage(Number(feeTemplate.default_value || 0))
                      : formatCurrency(Number(feeTemplate.default_value || 0))
                    : ''
                })
              }}
              options={
                feeTemplateOptions?.filter((feeTemplate) => {
                  return !contractFeesFields?.some((contractFee) => contractFee.fee_template_id === feeTemplate.value)
                }) || []
              }
              onInputChange={setSearchFeeTemplate}
              isDisabled={updateContractFeeDetailsIsPending}
            />

            {contractFeesFields?.map((contractFee, index) => (
              <div key={contractFee.id || `fee_${index}`} className="flex gap-x-2 w-full ">
                <Button
                  name={`remove_contract_fee_${index}`}
                  type="button"
                  variant="outline-error"
                  className="size-[3.25rem] mt-5"
                  onClick={() => {
                    if (contractFee.id) {
                      setDeletedFees((prev) => [...prev, { id: contractFee.id, _destroy: true }])
                    }

                    removeContractFee(index)
                  }}
                  disabled={updateContractFeeDetailsIsPending}
                >
                  <XIcon />
                </Button>

                <div className='flex flex-col gap-4 w-full'>
                  <div className='flex gap-2'>
                    <div className="w-3/4 flex flex-col gap-y-2">
                      <TextField label="Categoria" defaultValue={contractFee.display_name} className="w-full" disabled />

                      {contractFee.calculate_as === 'percentage' && (
                        <Checkbox
                          name={`contract.contract_fees_attributes.${index}.main_fee`}
                          label="Tarifa principal"
                          className="gap-2"
                          labelClassName="text-[12px] font-light italic"
                          checked={!!contractFee.main_fee}
                          errorMessage={errors?.contract?.contract_fees_attributes?.[index]?.main_fee?.message || undefined}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            updateContractFee(index, { ...contractFee, main_fee: event.target.checked })
                          }}
                          disabled={updateContractFeeDetailsIsPending}
                        />
                      )}
                    </div>

                    <Controller
                      name={`contract.contract_fees_attributes.${index}.value`}
                      control={control}
                      disabled={updateContractFeeDetailsIsPending}
                      render={({ field }) => contractFee.calculate_as === 'fixed' ? (
                        <CurrencyTextField
                          label="Valor"
                          className="w-1/4"
                          {...field}
                          errorMessage={errors?.contract?.contract_fees_attributes?.[index]?.value?.message || undefined}
                          markAsRequired={!contractFee.id}
                          allowNegative
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            updateContractFee(index, { ...contractFee, value: event.target.value })
                          }}
                        />
                      ) : (
                        <UnitTextField
                          unit="%"
                          decimals={2}
                          minValue={-100}
                          maxValue={100}
                          allowNegative
                          label="Valor"
                          className="w-1/4"
                          {...field}
                          errorMessage={errors?.contract?.contract_fees_attributes?.[index]?.value?.message || undefined}
                          markAsRequired={!contractFee.id}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            updateContractFee(index, { ...contractFee, value: event.target.value })
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            ))}
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
